import { createMutation } from "@steto-care/steto-query";

import { AuthRepository, LoginIdentifier } from "@/io/repository/AuthRepository";

export class AuthQueries {
  constructor(private readonly authRepository: AuthRepository = new AuthRepository()) {}

  /**
   * In-flight refresh, shared by every caller.
   * The api rotates the refresh token and blacklists the presented one, so two
   * concurrent refreshes would invalidate each other: one wins, the other 401s,
   * and whichever settles last decides whether the user stays logged in.
   * Callers are React StrictMode double-invoking InitAuth, and concurrent 401s.
   */
  private refreshInFlight: Promise<string | null> | undefined;

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  useRefreshTokens = createMutation("use-refresh-token", (): Promise<string | null> => {
    this.refreshInFlight ??= this.doRefresh().finally(() => {
      this.refreshInFlight = undefined;
    });
    return this.refreshInFlight;
  });

  useLogout = createMutation("use-logout", async () => {
    const refresh = await this.authRepository.getRefreshToken();
    try {
      if (refresh) {
        await this.authRepository.revokeRefreshToken(refresh);
      }
    } finally {
      // Drop the token even when the revoke call fails, otherwise a reload
      // restores the session the user just logged out of.
      await this.authRepository.deleteRefreshToken();
    }
  });

  useRequestLoginCode = createMutation(
    "use-request-login-code",
    (identifier: LoginIdentifier) => this.authRepository.requestLoginCode(identifier),
    { retry: false },
  );

  useVerifyLoginCode = createMutation(
    "use-verify-login-code",
    async ({ identifier, code }: { identifier: LoginIdentifier; code: string }) => {
      const token = await this.authRepository.verifyLoginCode(identifier, code);
      await this.authRepository.setRefreshToken(token.refresh);
      return token.access;
    },
    { retry: false },
  );

  private doRefresh = async (): Promise<string | null> => {
    const token = await this.authRepository.getRefreshToken();
    if (!token) {
      return null; // No session to restore: don't call the api at all.
    }
    const tokens = await this.authRepository.refreshTokens(token);
    if (tokens.refresh) {
      await this.authRepository.setRefreshToken(tokens.refresh);
    }
    return tokens.access;
  };
}
